/**
 * What the page's sound reports for tests (ADR-085 decision 12), as `window.game.audio` and `window.gameAudio` on a
 * development page alone, since the sounds asked for include other players' footsteps and blows: how many sounds were asked
 * for, how many started and how many variants have decoded, the last 32 asked for with their hook, channel and
 * whether a buffer started, whether the context is unlocked, the music track playing, each level the mixer has in
 * force, which is what is heard, kept or not, and each voice playing with the level and pan the mixer last asked of
 * it (the amendment for P1.12-T10). Entries are written in place, so recording allocates nothing.
 */

/**
 * One sound asked for.
 */
export interface AudioEntry {
  hook: string;
  channel: string;
  played: boolean;
}

/**
 * One voice playing: its hook and channel, whether it is placed in the world, and the level, 0 to 1 under its
 * channel's volume, and the pan, -1 to 1, the mixer last asked of it, which the audio eases to.
 */
export interface AudioVoice {
  readonly hook: string;
  readonly channel: string;
  readonly placed: boolean;
  readonly level: number;
  readonly pan: number;
}

/**
 * The report.
 */
export interface AudioReport {
  requested: number;
  played: number;
  decoded: number;
  unlocked: boolean;
  music: string | null;
  /** Master's and each channel's level in force in the mixer, by id, built when read; none until the page's sound sets it. */
  levels: () => Readonly<Record<string, { readonly volume: number; readonly muted: boolean }>>;
  /** The audio context's state as the browser gives it (`running`, `suspended`, `closed`), or `none` before the first press has made it. */
  state: () => string;
  /** Whether the page's sound is held to one sound, as maintenance holds it. */
  held: () => boolean;
  /** The voices playing now, in channel then voice order, built when read; none until the page's sound sets it. */
  voices: () => readonly AudioVoice[];
  /** The last sounds asked for, oldest first, built when read. */
  readonly log: () => readonly AudioEntry[];
  /**
   * Records a sound asked for.
   *
   * @param hook The hook asked for.
   * @param channel Its channel, empty when it has none.
   * @param played Whether a buffer started.
   */
  readonly record: (hook: string, channel: string, played: boolean) => void;
}

const SIZE = 32;

/**
 * Makes the report.
 *
 * @returns A report with nothing asked for.
 */
export function createAudioReport(): AudioReport {
  const entries: AudioEntry[] = Array.from({ length: SIZE }, () => ({ hook: "", channel: "", played: false }));
  let next = 0;
  let count = 0;
  const report: AudioReport = {
    requested: 0,
    played: 0,
    decoded: 0,
    unlocked: false,
    music: null,
    levels: () => ({}),
    state: () => "none",
    held: () => false,
    voices: () => [],
    log: () => {
      const out: AudioEntry[] = [];
      for (let index = 0; index < count; index += 1) {
        const entry = entries[(next - count + index + SIZE) % SIZE] as AudioEntry;
        out.push({ hook: entry.hook, channel: entry.channel, played: entry.played });
      }
      return out;
    },
    record: (hook, channel, played) => {
      const entry = entries[next] as AudioEntry;
      entry.hook = hook;
      entry.channel = channel;
      entry.played = played;
      next = (next + 1) % SIZE;
      count = Math.min(SIZE, count + 1);
      report.requested += 1;
      if (played) {
        report.played += 1;
      }
    },
  };
  return report;
}
